import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { trackEvent } from '../lib/analytics';
import { useT } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const isPaypal = params.get('provider') === 'paypal';
  const paypalSubId = params.get('subscription_id');
  const [state, setState] = useState('checking'); // checking | paid | expired | error
  const attempts = useRef(0);

  useEffect(() => {
    const statusUrl = isPaypal && paypalSubId
      ? `${API}/api/payments/paypal/status/${paypalSubId}`
      : sessionId ? `${API}/api/payments/checkout/status/${sessionId}` : null;
    if (!statusUrl) { setState('error'); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState('error'); return; }
      attempts.current += 1;
      try {
        const r = await fetch(statusUrl);
        const d = await r.json();
        if (d.payment_status === 'paid') { trackEvent('stripe_payment_completed', { session_id: sessionId || paypalSubId }); setState('paid'); return; }
        if (d.status === 'expired' || d.status === 'cancelled') { setState('expired'); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, isPaypal, paypalSubId]);

  const C = {
    checking: { icon: <Loader2 className="w-10 h-10 text-[#F77F11] animate-spin" />, t: isEs ? 'Confirmando tu pago…' : 'Confirming your payment…', b: isEs ? `Un segundo — estamos verificando la transacción con ${isPaypal ? 'PayPal' : 'Stripe'}.` : `One second — we are verifying the transaction with ${isPaypal ? 'PayPal' : 'Stripe'}.` },
    paid: { icon: <CheckCircle2 className="w-10 h-10 text-emerald-500" />, t: isEs ? '¡Pago confirmado! 🎉' : 'Payment confirmed! 🎉', b: isEs ? 'Bienvenido a Kitxens. Nuestro equipo te contactará hoy mismo para el onboarding. Revisa tu email.' : 'Welcome to Kitxens. Our team will reach out today to start your onboarding. Check your email.' },
    expired: { icon: <XCircle className="w-10 h-10 text-red-500" />, t: isEs ? 'La sesión de pago expiró' : 'Payment session expired', b: isEs ? 'No se realizó ningún cargo. Puedes intentarlo de nuevo cuando quieras.' : 'You were not charged. You can try again anytime.' },
    error: { icon: <XCircle className="w-10 h-10 text-red-500" />, t: isEs ? 'No pudimos confirmar el pago' : 'We could not confirm the payment', b: isEs ? 'Si el cargo se realizó, recibirás confirmación por email. Escríbenos a info@kitxens.com si tienes dudas.' : 'If you were charged, you will receive an email confirmation. Contact info@kitxens.com with any questions.' },
  }[state];

  return (
    <div className="pt-28 md:pt-32 min-h-[70vh]" data-testid="payment-success-page">
      <div className="max-w-lg mx-auto px-6 py-16 text-center">
        <div className="mx-auto w-20 h-20 rounded-3xl bg-white border border-neutral-200 shadow-xl flex items-center justify-center">{C.icon}</div>
        <h1 className="mt-6 text-3xl font-bold text-neutral-900 tracking-[-0.01em]" data-testid="payment-status-title">{C.t}</h1>
        <p className="mt-3 text-neutral-600 text-[15px] leading-relaxed">{C.b}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white h-11 px-6 rounded-full text-[14px] font-bold transition-colors" data-testid="payment-home-cta">
            {isEs ? 'Ir al inicio' : 'Go home'} <ArrowRight className="w-4 h-4" />
          </Link>
          {(state === 'expired' || state === 'error') && (
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 hover:border-[#F77F11] hover:text-[#F77F11] h-11 px-6 rounded-full text-[14px] font-semibold transition-colors">
              {isEs ? 'Ver precios' : 'See pricing'}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
